(() => {
  if (window.ScQuickOrderGift) return;

  const RULE = '_sc_quick_order_gift_rule';
  const GIFT = '_sc_quick_order_gift';
  let config = {};
  try {
    config = JSON.parse(document.getElementById('sc-quick-order-gift-cart-config')?.textContent || '{}');
  } catch (_) {}
  const priceFactor = Number(config.priceFactor) > 0 ? Number(config.priceFactor) : 1;
  let pending = 0;
  let reconciliationQueue = Promise.resolve();
  let reconciliationRevision = 0;

  function begin() {
    pending += 1;
    document.querySelectorAll('#main-cart-items, #CartDrawer-CartItems').forEach((element) => {
      element.classList.add('cart__items--disabled');
    });
  }

  function end() {
    pending = Math.max(0, pending - 1);
    if (pending) return;
    document.querySelectorAll('#main-cart-items, #CartDrawer-CartItems').forEach((element) => {
      element.classList.remove('cart__items--disabled');
    });
  }

  // A cart mutation can replace checkout buttons, so guard their events instead
  // of retaining references to buttons from an earlier section render.
  document.addEventListener('click', (event) => {
    if (pending && event.target.closest('[name="checkout"], a[href*="/checkout"]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
  document.addEventListener('submit', (event) => {
    if (pending && (event.submitter?.name === 'checkout' || event.target.matches('#cart, #CartDrawer-Form'))) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  async function request(path, body) {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}${path}`, {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const result = await response.json();
    if (!response.ok || result.status || result.errors) {
      throw new Error(result.description || result.message || result.errors || config.errorMessage);
    }
    return result;
  }

  function hasManagedItems(cart) {
    return Boolean(cart.attributes?.[RULE]) || cart.items?.some((item) => item.properties?.[RULE] || item.properties?.[GIFT] === '1');
  }

  function giftRule(cart) {
    // Current carts store activation in an attribute. Read earlier quick order
    // line metadata as a fallback for carts created before cart synchronization.
    const candidates = cart.attributes?.[RULE]
      ? [cart.attributes[RULE]]
      : (cart.items || []).map((item) => item.properties?.[RULE]).filter(Boolean);
    for (const candidate of candidates) {
      try {
        const rule = JSON.parse(candidate);
        if (Number(rule.variantId) > 0 && Number(rule.threshold) > 0 && rule.currency === cart.currency) return rule;
      } catch (_) {}
    }
    return null;
  }

  function eligibleSubtotal(cart, rule = giftRule(cart)) {
    const eligible = Array.isArray(rule?.eligibleVariantIds) ? new Set(rule.eligibleVariantIds.map(String)) : null;
    const gifts = window.ScQuickOrderCart.giftUnits(cart);
    return (cart.items || []).reduce((total, item) => {
      if (eligible ? !eligible.has(String(item.variant_id)) : item.properties?._sc_quick_order_source !== 'schnellbestellliste') return total;
      const quantity = Number(item.quantity) - (gifts.get(item.key) || 0);
      return total + Math.round(Number(item.original_price) * priceFactor) * quantity;
    }, 0);
  }

  async function activate(cart, rule) {
    if (!rule.enabled) return cart;
    return request('cart/update.js', {
      attributes: { [RULE]: JSON.stringify({
        variantId: rule.variantId, threshold: rule.threshold, currency: rule.currency,
        eligibleVariantIds: rule.eligibleVariantIds, discountCode: String(rule.discountCode || '').trim(),
      }) },
    });
  }

  function sectionRequest(sections) {
    return sections.length ? { sections: [...new Set(sections)].slice(0, 5), sections_url: window.location.pathname } : {};
  }

  function reconcile(cart, sections = []) {
    if (!hasManagedItems(cart)) return cart;
    config.active = true;
    const revision = reconciliationRevision;
    begin();
    const task = reconciliationQueue.catch(() => {}).then(async () => {
      try {
        // Another cart response may have arrived while a gift request was in
        // progress. Read the current cart before using that queued response.
        if (revision !== reconciliationRevision) {
          cart = sections.length
            ? await request('cart/update.js', sectionRequest(sections))
            : await request('cart.js');
        }
        return await reconcileCart(cart, sections);
      } finally {
        reconciliationRevision += 1;
        end();
      }
    });
    reconciliationQueue = task;
    return task;
  }

  async function reconcileCart(cart, sections) {
    let state = cart;
    try {
      const rule = giftRule(state);
      const reached = rule && eligibleSubtotal(state, rule) >= Number(rule.threshold);
      const owned = window.ScQuickOrderCart.giftUnits(state);
      const gifts = state.items.filter((item) => owned.has(item.key));
      const keeper = reached ? gifts.find((item) => String(item.variant_id) === String(rule.variantId)) : null;
      const updates = {};
      const stored = window.ScQuickOrderCart.storedRule(state);
      const allocated = window.ScQuickOrderCart.allocatedGiftLine(state);
      const clearActivation = state.attributes?.[RULE] && !window.ScQuickOrderCart.quantities(state).size;
      const resetAllocation = Number(stored?.gift?.quantity) === 1 && (!keeper || keeper !== allocated);
      const discountCode = String(stored?.discountCode || rule?.discountCode || '').trim();
      const discount = window.ScQuickOrderCart.discountValue(state, '', !keeper && discountCode ? [discountCode] : []);
      const removeDiscount = discount !== window.ScQuickOrderCart.discountValue(state);
      gifts.forEach((item) => {
        const quantity = item.quantity - owned.get(item.key) + (item === keeper ? 1 : 0);
        if (item.quantity !== quantity) updates[item.key] = quantity;
      });
      if (Object.keys(updates).length || clearActivation || resetAllocation || removeDiscount) {
        state = await request('cart/update.js', {
          updates,
          ...(removeDiscount ? { discount } : {}),
          ...(clearActivation || resetAllocation ? { attributes: {
            [RULE]: clearActivation ? '' : JSON.stringify({ ...stored, gift: { quantity: 0 } }),
          } } : {}),
          ...sectionRequest(sections),
        });
      }
      // Only the quick order submission may add a gift. Other cart changes
      // keep a qualifying gift or remove it; reaching the goal cannot restore it.
      return state;
    } catch (error) {
      error.cart = state;
      throw error;
    }
  }

  async function reconcileSafely(cart, sections) {
    try {
      return await reconcile(cart, sections);
    } catch (error) {
      // Keep successfully changed customer items visible if the gift is sold out.
      return { ...(error.cart || cart), scQuickOrderGiftError: config.errorMessage };
    }
  }

  async function applyDiscount(code, cart) {
    const state = await request('cart/update.js', { discount: window.ScQuickOrderCart.discountValue(cart, code) });
    const submitted = state.discount_codes?.find((discount) => discount.code.toLowerCase() === code.trim().toLowerCase());
    if (submitted?.applicable === false && window.ScQuickOrderCart.giftUnits(cart).size) {
      throw new Error(config.discountErrorMessage);
    }
    return state;
  }

  async function afterAdd(response, sections) {
    if (!config.active) {
      notify();
      return response;
    }
    begin();
    try {
      const cart = await request('cart.js');
      const state = await reconcileSafely(cart, sections);
      notify(state);
      return state === cart ? response : { ...response, ...state, sections: state.sections || response.sections };
    } catch (_) {
      return { ...response, scQuickOrderGiftError: config.errorMessage };
    } finally {
      end();
    }
  }

  function sectionsToRender() {
    const sections = [];
    document.querySelectorAll('cart-items, cart-drawer-items').forEach((element) => {
      if (typeof element.getSectionsToRender === 'function') sections.push(...element.getSectionsToRender());
    });
    return sections.filter((section, index) => sections.findIndex((candidate) => candidate.id === section.id) === index);
  }

  function showError(cart) {
    if (!cart.scQuickOrderGiftError) return;
    document.querySelectorAll('#cart-errors, #CartDrawer-CartErrors').forEach((element) => {
      element.textContent = cart.scQuickOrderGiftError;
    });
  }

  function notify(cart) {
    if (cart) config.active = Boolean(hasManagedItems(cart));
    window.dispatchEvent?.(new CustomEvent('sc:cart-updated', { detail: { cart } }));
  }

  function renderSections(cart, sections) {
    sections.forEach((section) => {
      const html = cart.sections?.[section.section];
      if (!html) return;
      const target = document.getElementById(section.id);
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
      if (target && source) (target.querySelector(section.selector) || target).innerHTML = source.innerHTML;
    });
    document.querySelectorAll('cart-items, cart-drawer-items, cart-drawer, #main-cart-footer').forEach((element) => {
      element.classList.toggle('is-empty', cart.item_count === 0);
      if ('currentItemCount' in element) element.currentItemCount = cart.item_count;
    });
    showError(cart);
  }

  async function refresh() {
    begin();
    try {
      const cart = await request('cart.js');
      if (!hasManagedItems(cart)) return;
      const sections = sectionsToRender();
      const state = await reconcileSafely(cart, sections.map((section) => section.section));
      if (state !== cart) renderSections(state, sections);
      notify(state);
    } catch (_) {
      showError({ scQuickOrderGiftError: config.errorMessage });
    } finally {
      end();
    }
  }

  window.ScQuickOrderGift = { request, activate, reconcile, reconcileSafely, applyDiscount, afterAdd, hasManagedItems, showError, notify, begin, end };
  window.addEventListener('pageshow', (event) => {
    if (event.persisted && config.active) refresh();
  });
  if (config.active) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true });
    else refresh();
  }
})();
